import {
  DEFAULT_REPORT_SECTION,
  REPORT_SECTION_LIST,
} from "../../classes/constants/reportConstants";
import {
  clearAllReportRecords,
  deleteReportRecord,
  deleteReportRecordsBySession,
  deleteReportRecordsNotInSession,
  getReportRecordsBySession,
  saveReportRecord,
} from "./ReportStore";

const SESSION_STORAGE_KEY = "spinesim-report-session-id";

const generateId = () => {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `report-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
};

const nowIsoString = () => new Date().toISOString();

class ReportManager {
  constructor() {
    this.sessionId = null;
    this.initialized = false;
    this.subscribers = new Set();
    this.cachedRecordsBySection = this.createEmptySectionMap();
    this.sectionIndexById = new Map();
  }

  createEmptySectionMap() {
    return REPORT_SECTION_LIST.reduce((acc, section) => {
      acc[section] = [];
      return acc;
    }, {});
  }

  normalizeSection(section) {
    if (!section || !REPORT_SECTION_LIST.includes(section)) {
      return DEFAULT_REPORT_SECTION;
    }
    return section;
  }

  sortSectionRecords(section) {
    if (!this.cachedRecordsBySection[section]) {
      return;
    }

    this.cachedRecordsBySection[section].sort((a, b) =>
      (a?.createdAt || "").localeCompare(b?.createdAt || "")
    );
  }

  sortAllSections() {
    REPORT_SECTION_LIST.forEach(section => this.sortSectionRecords(section));
  }

  ensureSessionId() {
    if (this.sessionId) {
      return { sessionId: this.sessionId, isNew: false };
    }

    if (typeof window === "undefined") {
      return { sessionId: null, isNew: false };
    }

    try {
      const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (existing) {
        this.sessionId = existing;
        return { sessionId: existing, isNew: false };
      }

      const newId = generateId();
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, newId);
      this.sessionId = newId;
      return { sessionId: newId, isNew: true };
    } catch (error) {
      this.sessionId = this.sessionId || generateId();
      return { sessionId: this.sessionId, isNew: true };
    }
  }

  async initialize() {
    const { sessionId, isNew } = this.ensureSessionId();

    if (!sessionId) {
      return this.getSnapshot();
    }

    if (!this.initialized) {
      if (isNew) {
        await deleteReportRecordsNotInSession(sessionId);
      }

      const records = await getReportRecordsBySession(sessionId);
      this.cachedRecordsBySection = this.createEmptySectionMap();
      this.sectionIndexById.clear();

      records.forEach(record => {
        const section = this.normalizeSection(record.section);
        const normalizedRecord = { ...record, section };
        this.cachedRecordsBySection[section].push(normalizedRecord);
        this.sectionIndexById.set(record.id, section);
      });

      this.sortAllSections();
      this.initialized = true;
    }

    return this.getSnapshot();
  }

  subscribe(listener) {
    if (typeof listener !== "function") {
      throw new Error("ReportManager.subscribe expects a function");
    }

    this.subscribers.add(listener);

    return () => {
      this.subscribers.delete(listener);
    };
  }

  notify(event) {
    this.subscribers.forEach(listener => {
      try {
        listener(event);
      } catch (error) {}
    });
  }

  buildRecord(section, blob, metadata = {}) {
    const createdAt = nowIsoString();
    return {
      id: metadata?.id || generateId(),
      sessionId: this.sessionId,
      section: this.normalizeSection(section),
      createdAt,
      fileName:
        metadata?.fileName || `report_${createdAt.replace(/[:.]/g, "-")}.png`,
      source: metadata?.source || null,
      metadata,
      blob,
    };
  }

  getSnapshot() {
    const snapshot = {};
    REPORT_SECTION_LIST.forEach(section => {
      snapshot[section] = [...(this.cachedRecordsBySection[section] || [])];
    });
    return snapshot;
  }

  async addBlob({ section, blob, metadata = {} }) {
    if (!(blob instanceof Blob)) {
      throw new Error("ReportManager.addBlob expects a Blob instance");
    }

    const { sessionId } = this.ensureSessionId();
    if (!sessionId) {
      throw new Error("ReportManager.addBlob could not resolve session");
    }

    if (!this.initialized) {
      await this.initialize();
    }

    const record = this.buildRecord(section, blob, metadata);
    const { section: normalizedSection } = record;

    const { storage } = await saveReportRecord(record);

    if (storage !== "indexeddb") {
      throw new Error(
        "ReportManager.addBlob requires IndexedDB persistence but it is unavailable"
      );
    }

    const existingSection = this.sectionIndexById.get(record.id);
    if (existingSection) {
      const existingEntries =
        this.cachedRecordsBySection[existingSection] || [];
      this.cachedRecordsBySection[existingSection] = existingEntries.filter(
        existingRecord => existingRecord.id !== record.id
      );
    }

    const normalizedEntries =
      this.cachedRecordsBySection[normalizedSection] || [];
    this.cachedRecordsBySection[normalizedSection] = [
      ...normalizedEntries.filter(
        existingRecord => existingRecord.id !== record.id
      ),
      record,
    ];
    this.sectionIndexById.set(record.id, normalizedSection);
    this.sortSectionRecords(normalizedSection);

    this.notify({ type: "added", record });
    return record;
  }

  async remove(id) {
    if (!id) {
      return;
    }

    const { sessionId } = this.ensureSessionId();
    if (!sessionId) {
      return;
    }

    const section = this.sectionIndexById.get(id);
    if (!section) {
      return;
    }

    this.cachedRecordsBySection[section] = this.cachedRecordsBySection[
      section
    ].filter(record => record.id !== id);
    this.sectionIndexById.delete(id);

    await deleteReportRecord(id);
    this.notify({ type: "removed", id, section });
  }

  async clearSection(section) {
    const normalizedSection = this.normalizeSection(section);
    const records = this.cachedRecordsBySection[normalizedSection] || [];

    if (!records.length) {
      return;
    }

    await Promise.all(records.map(record => deleteReportRecord(record.id)));

    records.forEach(record => this.sectionIndexById.delete(record.id));
    this.cachedRecordsBySection[normalizedSection] = [];

    this.notify({ type: "section-cleared", section: normalizedSection });
  }

  async clearSession() {
    const { sessionId } = this.ensureSessionId();
    if (!sessionId) {
      return;
    }

    await deleteReportRecordsBySession(sessionId);
    this.cachedRecordsBySection = this.createEmptySectionMap();
    this.sectionIndexById.clear();
    this.notify({ type: "cleared" });
  }

  async clearAllSessions() {
    await clearAllReportRecords();
    this.cachedRecordsBySection = this.createEmptySectionMap();
    this.sectionIndexById.clear();
    this.notify({ type: "cleared" });
  }

  async reload() {
    const { sessionId } = this.ensureSessionId();
    if (!sessionId) {
      this.cachedRecordsBySection = this.createEmptySectionMap();
      this.sectionIndexById.clear();
      return this.getSnapshot();
    }

    const records = await getReportRecordsBySession(sessionId);
    this.cachedRecordsBySection = this.createEmptySectionMap();
    this.sectionIndexById.clear();

    records.forEach(record => {
      const section = this.normalizeSection(record.section);
      const normalizedRecord = { ...record, section };
      this.cachedRecordsBySection[section].push(normalizedRecord);
      this.sectionIndexById.set(record.id, section);
    });

    this.sortAllSections();
    this.notify({
      type: "reloaded",
      recordsBySection: this.getSnapshot(),
    });

    return this.getSnapshot();
  }

  getSectionRecords(section) {
    const normalizedSection = this.normalizeSection(section);
    return [...(this.cachedRecordsBySection[normalizedSection] || [])];
  }

  getAllRecords() {
    return this.getSnapshot();
  }
}

const reportManager = new ReportManager();
export default reportManager;
